import React from 'react';
import { Date as CalendarIcon } from '@wix/wix-ui-icons-common';
import {
  dateTimeFormat,
  SupportedWixLocales,
} from '@wix/design-systems-locale-utils';
import { Input, InputProps } from '../../Input';
import { WixStyleReactEnvironmentContext } from '../../WixStyleReactEnvironmentProvider/context';
import { parseStrictInputValue, parseDate } from './utils';
import { dataHooks } from './constants';
import { DateInputProps } from './DateInput.types';
import { IconThemeContext } from '../../WixDesignSystemIconThemeProvider/IconThemeContext';

type ValidationType = 'valid' | 'formatError' | 'outOfBoundsError';

type DateInputState = {
  validationType: ValidationType;
  inputValue: string;
  statusMessage?: string;
};

class DateInput extends React.PureComponent<DateInputProps, DateInputState> {
  static displayName = 'DateInput';

  static contextType = WixStyleReactEnvironmentContext;
  context!: React.ContextType<typeof WixStyleReactEnvironmentContext>;
  constructor(props: DateInputProps) {
    super(props);
    this.state = {
      validationType: 'valid',
      inputValue: '',
      statusMessage: undefined,
    };
  }

  componentDidMount() {
    this.setState({
      inputValue: this._getFormattedDate(this.props.value),
    });
  }

  componentDidUpdate(prevProps: DateInputProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        inputValue: this._getFormattedDate(this.props.value),
      });
      if (this.state.validationType !== 'valid') {
        this.setState({
          validationType: 'valid',
          statusMessage: undefined,
        });
      }
    }
  }

  _getFormattedDate = (value?: Date | string) => {
    const { dateStyle } = this.props;

    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (dateStyle === 'long') {
      return dateTimeFormat.getLongDate(this._getLocale(), value);
    }

    if (dateStyle === 'medium') {
      return dateTimeFormat.getMediumDate(this._getLocale(), value);
    }

    return dateTimeFormat.getShortDate(this._getLocale(), value);
  };

  _handleBlur = () => {
    const date = parseStrictInputValue(
      this.state.inputValue,
      this.props.dateStyle,
      this._getLocale(),
    );
    if (date.parsedDate) {
      this.setState({
        inputValue: this._getFormattedDate(date.parsedDate),
      });
    }

    if (this.props.validate || this.props.onValidate) {
      this._handleValidation(date);
    }

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  _handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      inputValue: event.target.value,
    });

    const date = parseDate(
      event.target.value,
      this.props.dateStyle,
      this._getLocale(),
    );
    if (date) {
      if (this.props.onChange) {
        this.props.onChange({ dateVal: date });
      }
    }
  };

  _handleValidation = (date: {
    parsedDate?: Date | null;
    dateFormat: string;
  }) => {
    let validationType: DateInputState['validationType'] = 'valid';
    if (!date.parsedDate) {
      validationType = 'formatError';
      this.setState({
        validationType,
        statusMessage: `Invalid date. Please match "${date.dateFormat}" format`,
      });
    } else if (
      this.props.excludePastDates &&
      date.parsedDate &&
      date.parsedDate.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
    ) {
      validationType = 'outOfBoundsError';
      this.setState({
        validationType,
        statusMessage: 'Only today and future dates are allowed',
      });
    } else {
      this.setState({
        validationType,
        statusMessage: undefined,
      });
    }

    if (this.props.onValidate) {
      this.props.onValidate({
        validationType,
        format: validationType !== 'valid' ? date.dateFormat : undefined,
        value: this.state.inputValue,
      });
    }
  };

  _handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({ inputValue: '' });
    if (this.props.onClear) {
      this.props.onClear(event);
    }
  };

  _handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const date = parseDate(
      this.state.inputValue,
      this.props.dateStyle,
      this._getLocale(),
    );
    this.props.onKeyDown?.(event, date);
  };

  _getLocale(): SupportedWixLocales {
    return (
      this.props.locale ||
      ((this.context.locale || 'en') as SupportedWixLocales)
    );
  }

  render() {
    const {
      customInput,
      focusOnClearClick,
      onChange,
      status,
      statusMessage,
      validate,
      ...rest
    } = this.props;

    return (
      <IconThemeContext.Consumer>
        {({ icons = {} }) => {
          const DateIcon = icons.DateInput?.Date || CalendarIcon;

          const inputProps: InputProps = {
            focusOnClearClick: !!focusOnClearClick,
            autoSelect: false,
            prefix: (
              <Input.IconAffix dataHook={dataHooks.icon}>
                <DateIcon />
              </Input.IconAffix>
            ),
            status:
              validate && this.state.validationType !== 'valid' && !status
                ? 'error'
                : status,
            statusMessage:
              validate && !statusMessage
                ? this.state.statusMessage
                : statusMessage,
            ...rest,
            value: this.state.inputValue,
            onBlur: this._handleBlur,
            onChange: this._handleChange,
            onClear: this.props.onClear ? this._handleClear : undefined,
            onKeyDown: this._handleKeyDown,
            ariaLabel: this.state.inputValue || this.props.placeholder,
            ...(customInput ? customInput.props : {}),
          };
          return React.cloneElement(customInput || <Input />, inputProps);
        }}
      </IconThemeContext.Consumer>
    );
  }
}

export { DateInput };
